/**
 * Every row, in pages — because PostgREST answers at most 1000 per request.
 *
 * Verified on this project on 2026-09-14 with a 1500-row probe table read
 * through the REST API: HTTP 206, `content-range: 0-999/1500`, 1000 rows
 * back. A plain `select()` on a table past a thousand rows is a SILENT
 * partial read — no error, no toast, the missing rows simply not there. For
 * `sale_items` that is COGS and profit computed on a subset; for a stocktake
 * it is products that were counted and vanish from the review; for the
 * backup it is a file the owner believes complete. The earlier audit had
 * concluded the cap was not set here; it was wrong, and this file is what
 * that mistake taught: verify a limit by hitting it, not by reading config.
 *
 * `page(from, to)` must carry a DETERMINISTIC order — a unique column, or a
 * timestamp with `id` as a tiebreaker. Paging over ties skips and repeats
 * rows at page edges, which is the same silent error in a smaller coat.
 */

/** PostgREST's ceiling here. Asking for exactly it keeps the request count
 *  minimal; asking for more would just be capped. */
export const PAGE = 1000

/** 500 × 1000 — far past any shop, and finite. Past it we refuse the read
 *  rather than return a partial one, loudly. */
const MAX_PAGES = 500

/** Pages asked for at once, at most, once a read is known to be long. */
export const WINDOW = 4

type PageResult<T> = { data: T[] | null; error: unknown }

/**
 * Pages are read one, one, then two, then four at a time (2026-09-26). One
 * after another, a 10 000-product shop waited for eleven round trips before
 * the till opened; now five. The first two go alone on purpose: almost every
 * table fits in one or two pages, and those reads cost exactly what they
 * always did — no empty pages asked for in parallel. The rows come back in
 * page order whatever order the answers arrive in, and the read still ends at
 * the first short page: a page past it that was already asked for is ignored.
 */
export async function allRows<T>(
  page: (from: number, to: number) => PromiseLike<PageResult<T>>,
): Promise<{ data: T[] | null; error: unknown }> {
  const out: T[] = []
  let next = 0
  while (next < MAX_PAGES) {
    const width = Math.min(next < 2 ? 1 : Math.min(next, WINDOW), MAX_PAGES - next)
    const results = await Promise.all(
      Array.from({ length: width }, (_, k) => page((next + k) * PAGE, (next + k) * PAGE + PAGE - 1)),
    )
    for (const { data, error } of results) {
      if (error) return { data: null, error }
      const got = data ?? []
      for (const row of got) out.push(row)
      if (got.length < PAGE) return { data: out, error: null }
    }
    next += width
  }
  return { data: null, error: new Error(`allRows: more than ${MAX_PAGES * PAGE} rows — refusing a partial read`) }
}

/**
 * `allRows`, plus a follow-up read for every page, started the moment that
 * page arrives (126, 2026-09-30) — the lines of 1 000 sales while the next
 * pages of sales are still on their way, instead of after the last of them.
 * The scale test's boot read 45 000 sales in 9.6 s and only THEN their lines,
 * for 19 s more.
 *
 * Follow-ups come back in page order. One started for a page past the end of
 * the read (asked for in parallel, then ignored by `allRows`) is dropped with
 * its page. A follow-up that throws is an error, never an unhandled rejection;
 * the first error of either read wins.
 */
export async function allRowsWith<T, U>(
  page: (from: number, to: number) => PromiseLike<PageResult<T>>,
  follow: (rows: T[]) => PromiseLike<PageResult<U>>,
): Promise<{ rows: PageResult<T>; followed: PageResult<U> }> {
  const started = new Map<number, Promise<PageResult<U>>>()
  const rows = await allRows<T>((from, to) =>
    Promise.resolve(page(from, to)).then((res) => {
      if (!res.error && res.data && res.data.length > 0) {
        const data = res.data
        started.set(from, Promise.resolve()
          .then(() => follow(data))
          .then((r) => r, (error: unknown) => ({ data: null, error })))
      }
      return res
    }))
  if (rows.error) return { rows, followed: { data: null, error: rows.error } }
  const n = rows.data?.length ?? 0
  const out: U[] = []
  for (const from of [...started.keys()].sort((a, b) => a - b)) {
    if (from >= n) continue
    const { data, error } = await started.get(from)!
    if (error) return { rows, followed: { data: null, error } }
    for (const r of data ?? []) out.push(r)
  }
  return { rows, followed: { data: out, error: null } }
}

/** Sales per `sale_items_of` call (125). ~1.9 lines a sale keeps a chunk inside
 *  one 1 000-row page; a bigger chunk still pages, it just costs a round trip. */
export const SALE_CHUNK = 250

/**
 * The lines of the given sales, read BY SALE (125, 2026-09-30) — chunks of
 * SALE_CHUNK ids, WINDOW chunks at a time, each chunk paged like any read.
 * The 6-store scale test: `sale_items_between(90 days)` recomputed all 83 732
 * lines for every 1 000-row page (6.6 s a page) and the boot died on the
 * statement timeout; 250 sales' lines by id come back in 5 ms. Same
 * `{ data, error }` as `allRows`, lines in chunk order; the first error wins.
 */
export async function linesOfSales<T>(
  page: (ids: string[], from: number, to: number) => PromiseLike<PageResult<T>>,
  saleIds: string[],
): Promise<{ data: T[] | null; error: unknown }> {
  const chunks: string[][] = []
  for (let i = 0; i < saleIds.length; i += SALE_CHUNK) chunks.push(saleIds.slice(i, i + SALE_CHUNK))
  const out: T[] = []
  for (let i = 0; i < chunks.length; i += WINDOW) {
    const results = await Promise.all(chunks.slice(i, i + WINDOW).map((ids) => allRows<T>((a, b) => page(ids, a, b))))
    for (const { data, error } of results) {
      if (error) return { data: null, error }
      for (const row of data ?? []) out.push(row)
    }
  }
  return { data: out, error: null }
}
